// src/parametres/SauvegardeRestauration.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
    Card, Row, Col, Button, Spinner, Table,
    Form, Pagination, Tooltip, OverlayTrigger, Modal
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function SauvegardeRestauration() {
    const navigate = useNavigate();
    const [sauvegardes, setSauvegardes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [restoring, setRestoring] = useState(false);
    const [stats, setStats] = useState(null);
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });

    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(8);

    const [showRestoreModal, setShowRestoreModal] = useState(false);
    const [fileToRestore, setFileToRestore] = useState(null);

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    useEffect(() => {
        fetchSauvegardes();
        fetchStats();
    }, []);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchSauvegardes = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=sauvegardes`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setSauvegardes(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=stats_sauvegardes`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    /* ============================================================
       ACTIONS
       ============================================================ */
    const handleRestoreClick = (fichier) => {
        setFileToRestore(fichier);
        setShowRestoreModal(true);
    };

    const handleRestoreConfirm = async () => {
        if (!fileToRestore) return;
        setShowRestoreModal(false);
        setRestoring(true);

        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'restaurer_sauvegarde',
                        fichier: fileToRestore
                    })
                }
            );
            const text = await response.text();
            if (!response.ok) throw new Error(`Erreur HTTP ${response.status}: ${text.substring(0, 200)}`);
            if (!text || text.trim() === '') throw new Error("La réponse du serveur est vide");
            const result = JSON.parse(text);
            if (result.success) {
                showToast('success', 'Restauration réussie', 'La page va se recharger...');
                setTimeout(() => window.location.reload(), 3000);
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de la restauration");
            }
        } catch (err) {
            console.error("Erreur complète:", err);
            showToast('error', 'Erreur', err.message);
        } finally {
            setRestoring(false);
            setFileToRestore(null);
        }
    };

    const handleDownload = async (fichier) => {
        try {
            const token = localStorage.getItem("token");
            const downloadUrl = `${API_BASE_URL}/parametres.php?action=telecharger_sauvegarde&fichier=${encodeURIComponent(fichier)}&token=${token}`;
            const response = await fetch(downloadUrl, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error("Erreur lors du téléchargement");
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fichier;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            showToast('success', 'Téléchargement', `Téléchargement de ${fichier} démarré`);
        } catch (err) {
            showToast('error', 'Erreur', "Erreur lors du téléchargement : " + err.message);
        }
    };

    /* ============================================================
       HELPERS
       ============================================================ */
    const formatTaille = (octets) => {
        if (!octets) return '—';
        if (octets < 1024) return octets + ' o';
        if (octets < 1024 * 1024) return (octets / 1024).toFixed(1) + ' Ko';
        return (octets / (1024 * 1024)).toFixed(1) + ' Mo';
    };

    const getNiveauRisque = (date) => {
        const jours = Math.floor((new Date() - new Date(date)) / (1000 * 60 * 60 * 24));
        if (jours <= 7) {
            return { gradient: 'linear-gradient(135deg, #10b981, #059669)', text: 'Récente', icon: 'ri-check-line' };
        }
        if (jours <= 30) {
            return { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', text: 'Mois', icon: 'ri-time-line' };
        }
        return { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', text: 'Ancienne', icon: 'ri-alert-line' };
    };

    const filteredSauvegardes = useMemo(() => {
        return sauvegardes.filter(s =>
            s.nom?.toLowerCase().includes(searchText.toLowerCase()) ||
            formatDate(s.date).toLowerCase().includes(searchText.toLowerCase())
        );
    }, [sauvegardes, searchText]);

    const totalPages = Math.ceil(filteredSauvegardes.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = filteredSauvegardes.slice(startIndex, startIndex + itemsPerPage);

    /* ============================================================
       LOADING — identique à l'original
       ============================================================ */
    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center" style={{ maxWidth: '400px' }}>
                            <div className="loading-ring-wrapper mb-4">
                                <div className="loading-ring"></div>
                                <div className="loading-ring-inner"></div>
                                <i className="ri-loader-4-fill loading-icon"></i>
                            </div>
                            <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                                Chargement des sauvegardes...
                            </h5>
                            <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                            <div className="mx-auto mt-4" style={{ width: '80%' }}>
                                <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                    <div
                                        className="progress-bar progress-bar-striped progress-bar-animated"
                                        style={{
                                            width: '100%',
                                            background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                            backgroundSize: '200% 100%',
                                            animation: 'shimmer 1.5s linear infinite'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
                <style>{`
                    .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                    .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                    .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                    .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                    @keyframes spin { to { transform: rotate(360deg); } }
                    @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                    @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                `}</style>
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                                <i className="ri-restart-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Restauration</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-stack-line"></i> {filteredSauvegardes.length} fichier(s)
                                    </span>
                                    {stats?.espace_total > 0 && (
                                        <span className="badge-modern" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                            <i className="ri-hard-drive-line"></i> {formatTaille(stats.espace_total)}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={fetchSauvegardes} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                            <Button className="btn-modern-primary" onClick={() => navigate('/parametres/sauvegarde/manuel')}>
                                <i className="ri-database-2-line"></i> Sauvegarde
                            </Button>
                        </div>
                    </div>

                    {/* ===== KPI CARDS ===== */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-primary">
                                    <div className="kpi-icon-charte"><i className="ri-database-2-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Total sauvegardes</span>
                                        <h3 className="kpi-value-charte">{stats.total || sauvegardes.length}</h3>
                                        <span className="kpi-unit-charte">fichiers</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-warning">
                                    <div className="kpi-icon-charte"><i className="ri-hard-drive-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Espace utilisé</span>
                                        <h3 className="kpi-value-charte">{formatTaille(stats.espace_total || 0)}</h3>
                                        <span className="kpi-unit-charte">stockage</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-success">
                                    <div className="kpi-icon-charte"><i className="ri-calendar-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Dernière sauvegarde</span>
                                        <h3 className="kpi-value-charte" style={{ fontSize: '0.9rem' }}>
                                            {stats.derniere_sauvegarde ? formatDate(stats.derniere_sauvegarde) : '—'}
                                        </h3>
                                        <span className="kpi-unit-charte">la plus récente</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-danger">
                                    <div className="kpi-icon-charte"><i className="ri-shield-check-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Niveau sécurité</span>
                                        <h3 className="kpi-value-charte">{stats.niveau_securite || 'Bon'}</h3>
                                        <span className="kpi-unit-charte">recommandations</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* ===== AVERTISSEMENT ===== */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="p-4">
                            <div className="info-banner-charte" style={{ background: '#fffbeb', borderColor: '#fde68a' }}>
                                <div className="info-banner-icon-charte" style={{ background: `${COLORS.warning}20`, color: COLORS.warning }}>
                                    <i className="ri-alert-line"></i>
                                </div>
                                <div>
                                    <h6 className="fw-bold mb-1" style={{ color: COLORS.warning }}>
                                        Attention — Opération irréversible
                                    </h6>
                                    <p className="small text-muted mb-0">
                                        La restauration d'une sauvegarde va remplacer toutes les données actuelles par celles
                                        de la sauvegarde sélectionnée. Cette action est définitive et ne peut pas être annulée.
                                    </p>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* ===== PROGRESSION ===== */}
                    {restoring && (
                        <Card className="border-0 shadow-sm rounded-3 mb-4">
                            <Card.Body className="p-4">
                                <div className="d-flex align-items-center gap-3 mb-3">
                                    <Spinner animation="border" style={{ color: COLORS.primary }} />
                                    <div>
                                        <strong className="d-block">Restauration en cours...</strong>
                                        <small className="text-muted">Cette opération peut prendre quelques instants</small>
                                    </div>
                                </div>
                                <div className="progress-bar-charte">
                                    <div className="progress-fill-charte" style={{ width: '100%', background: COLORS.primary }}></div>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* ===== FILTRES ===== */}
                    <Card className="filter-card-charte mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="5">
                                    <div className="search-box-charte">
                                        <i className="ri-search-line search-icon-charte"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher un fichier ou une date..."
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            className="search-input-charte"
                                        />
                                    </div>
                                </Col>
                                <Col md="3">
                                    <Form.Select
                                        value={itemsPerPage}
                                        onChange={(e) => {
                                            setItemsPerPage(parseInt(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                        className="filter-select-charte"
                                    >
                                        <option value="5">5 / page</option>
                                        <option value="8">8 / page</option>
                                        <option value="15">15 / page</option>
                                        <option value="30">30 / page</option>
                                    </Form.Select>
                                </Col>
                                <Col md="4" className="text-end">
                                    <span className="count-badge-charte">
                                        <i className="ri-file-list-line"></i>
                                        {filteredSauvegardes.length} fichier(s)
                                    </span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== TABLEAU ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="card-header-gradient">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-restart-line text-white fs-5"></i>
                                    <span className="fw-semibold text-white">Liste des sauvegardes</span>
                                    <span className="badge-header-light">{filteredSauvegardes.length}</span>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 table-charte">
                                    <thead>
                                        <tr>
                                            <th className="ps-4">Date</th>
                                            <th style={{ minWidth: '300px' }}>Fichier</th>
                                            <th className="text-center">Taille</th>
                                            <th className="text-center">Âge</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? (
                                            currentItems.map((s, index) => {
                                                const risque = getNiveauRisque(s.date);
                                                return (
                                                    <tr key={index}>
                                                        <td className="ps-4">
                                                            <div className="d-flex flex-column">
                                                                <small className="fw-semibold">
                                                                    <i className="ri-calendar-line me-1 text-muted"></i>
                                                                    {formatDate(s.date)}
                                                                </small>
                                                                <small className="text-muted">
                                                                    <i className="ri-time-line me-1"></i>
                                                                    {new Date(s.date).toLocaleTimeString('fr-FR')}
                                                                </small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className="reference-badge-charte">{s.nom}</span>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className="status-badge-charte" style={{ background: 'linear-gradient(135deg, #64748b, #475569)' }}>
                                                                <i className="ri-hard-drive-2-line"></i>
                                                                {formatTaille(s.taille)}
                                                            </span>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className="status-badge-charte" style={{ background: risque.gradient }}>
                                                                <i className={risque.icon}></i>
                                                                {risque.text}
                                                            </span>
                                                        </td>
                                                        <td className="text-center pe-4">
                                                            <div className="d-flex gap-2 justify-content-center">
                                                                <OverlayTrigger placement="top" overlay={<Tooltip>Restaurer</Tooltip>}>
                                                                    <button
                                                                        className="action-btn-charte"
                                                                        onClick={() => handleRestoreClick(s.nom)}
                                                                        disabled={restoring}
                                                                        style={{ '--btn-color': COLORS.warning }}
                                                                    >
                                                                        <i className="ri-restart-line"></i>
                                                                    </button>
                                                                </OverlayTrigger>
                                                                <OverlayTrigger placement="top" overlay={<Tooltip>Télécharger</Tooltip>}>
                                                                    <button
                                                                        className="action-btn-charte"
                                                                        onClick={() => handleDownload(s.nom)}
                                                                        style={{ '--btn-color': COLORS.primary }}
                                                                    >
                                                                        <i className="ri-download-line"></i>
                                                                    </button>
                                                                </OverlayTrigger>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="5">
                                                    <div className="empty-state-charte">
                                                        <div className="empty-state-icon-charte">
                                                            <i className="ri-inbox-line"></i>
                                                        </div>
                                                        <h6 className="fw-semibold mb-1">Aucune sauvegarde trouvée</h6>
                                                        <p className="text-muted small mb-3">
                                                            {searchText
                                                                ? "Aucun résultat ne correspond à votre recherche"
                                                                : "Créez d'abord une sauvegarde dans l'onglet 'Sauvegarde manuelle'"}
                                                        </p>
                                                        {!searchText && (
                                                            <Button className="btn-modern-primary" onClick={() => navigate('/parametres/sauvegarde/manuel')}>
                                                                <i className="ri-database-2-line me-1"></i> Créer une sauvegarde
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* ===== PAGINATION ===== */}
                    {totalPages > 1 && !loading && (
                        <div className="pagination-wrapper-charte mt-3">
                            <div className="pagination-info-charte">
                                Affichage de <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredSauvegardes.length)}</strong> sur <strong>{filteredSauvegardes.length}</strong> sauvegardes
                            </div>
                            <Pagination className="pagination-charte mb-0">
                                <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
                                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                    let pageNum;
                                    if (totalPages <= 5) pageNum = i + 1;
                                    else if (currentPage <= 3) pageNum = i + 1;
                                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                    else pageNum = currentPage - 2 + i;
                                    return (
                                        <Pagination.Item key={i} active={pageNum === currentPage} onClick={() => setCurrentPage(pageNum)}>
                                            {pageNum}
                                        </Pagination.Item>
                                    );
                                })}
                                <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
                            </Pagination>
                        </div>
                    )}

                    {/* ===== RECOMMANDATIONS ===== */}
                    {sauvegardes.length > 0 && (
                        <Card className="border-0 shadow-sm rounded-3 mt-4">
                            <Card.Body className="p-4">
                                <div className="info-banner-charte">
                                    <div className="info-banner-icon-charte" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                        <i className="ri-lightbulb-line"></i>
                                    </div>
                                    <div className="flex-grow-1">
                                        <h6 className="fw-semibold mb-1">Bonnes pratiques</h6>
                                        <p className="small text-muted mb-0">
                                            Pour une sécurité optimale, conservez au moins une sauvegarde par semaine et
                                            testez régulièrement la procédure de restauration.
                                        </p>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    )}
                </div>

                {/* ===== MODAL RESTAURATION ===== */}
                <Modal show={showRestoreModal} onHide={() => setShowRestoreModal(false)} centered className="modal-charte">
                    <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.warning}, #d97706)` }}>
                        <div className="d-flex align-items-center gap-3">
                            <div className="modal-header-icon"><i className="ri-restart-line"></i></div>
                            <div>
                                <Modal.Title className="fs-5 fw-bold text-white mb-0">Restaurer la sauvegarde</Modal.Title>
                                <p className="text-white-50 mb-0 small">Cette action est irréversible</p>
                            </div>
                        </div>
                    </Modal.Header>
                    <Modal.Body className="p-4">
                        <p className="text-center text-muted mb-4">
                            Vous êtes sur le point de restaurer la sauvegarde{' '}
                            <strong style={{ color: COLORS.warning }}>"{fileToRestore}"</strong>
                        </p>

                        <div className="summary-card-charte mb-3">
                            <div className="d-flex align-items-center gap-3">
                                <div className="categorie-avatar-charte" style={{ background: `linear-gradient(135deg, ${COLORS.warning}, #d97706)` }}>
                                    <i className="ri-database-2-line"></i>
                                </div>
                                <div className="flex-grow-1">
                                    <div className="fw-bold" style={{ color: COLORS.primaryDark }}>
                                        {fileToRestore}
                                    </div>
                                    <div className="text-muted small">
                                        <i className="ri-alert-line me-1"></i>
                                        Écrasement de toutes les données actuelles
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="alert-warning-charte mb-0">
                            <div className="d-flex align-items-center">
                                <div className="alert-warning-icon-charte"><i className="ri-alert-line"></i></div>
                                <div className="ms-3">
                                    <strong>Attention</strong>
                                    <p className="mb-0 small">
                                        La restauration va écraser TOUTES les données actuelles par celles de la sauvegarde.
                                        Assurez-vous d'avoir une sauvegarde récente de vos données actuelles.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                        <Button className="btn-modern-outline" onClick={() => setShowRestoreModal(false)}>
                            Annuler
                        </Button>
                        <Button className="btn-modern-warning" onClick={handleRestoreConfirm}>
                            <i className="ri-restart-line me-2"></i>Restaurer
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }

                /* ===== FILTRES ===== */
                .filter-card-charte {
                    background: linear-gradient(135deg, var(--primary-dark), var(--primary));
                    border-radius: 12px; border: none;
                    box-shadow: 0 8px 24px rgba(41,90,167,0.15);
                }
                .search-box-charte { position: relative; }
                .search-icon-charte {
                    position: absolute; top: 50%; left: 1rem;
                    transform: translateY(-50%);
                    color: var(--primary); font-size: 1rem;
                    z-index: 2; pointer-events: none;
                }
                .search-input-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem 0.6rem 2.75rem !important;
                    font-size: 0.9rem;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .search-input-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .filter-select-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem !important;
                    font-size: 0.85rem;
                    cursor: pointer;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .filter-select-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .count-badge-charte {
                    background: rgba(255,255,255,0.95); color: var(--primary);
                    padding: 0.6rem 1.25rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== HEADER GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }

                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                }
                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.3rem 0.75rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 600;
                    white-space: nowrap;
                }

                /* ===== PROGRESS ===== */
                .progress-bar-charte {
                    background: #e2e8f0;
                    border-radius: 6px;
                    height: 8px;
                    overflow: hidden;
                }
                .progress-fill-charte {
                    height: 100%;
                    border-radius: 6px;
                    transition: width 0.3s ease;
                }

                /* ===== ACTION BTN ===== */
                .action-btn-charte {
                    width: 36px; height: 36px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: white; border: 1.5px solid #e2e8f0;
                    color: #64748b; font-size: 1rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .action-btn-charte:hover {
                    border-color: var(--btn-color); color: var(--btn-color);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                }
                .action-btn-charte:disabled { opacity: 0.5; cursor: not-allowed; }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== PAGINATION ===== */
                .pagination-wrapper-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 1rem 1.5rem;
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    flex-wrap: wrap; gap: 1rem;
                }
                .pagination-info-charte { font-size: 0.8rem; color: #64748b; }
                .pagination-charte { gap: 6px; }
                .pagination-charte .page-item .page-link {
                    border-radius: 10px;
                    border: 1.5px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 0.9rem;
                    font-weight: 500; font-size: 0.85rem;
                    transition: all 0.2s;
                }
                .pagination-charte .page-item.active .page-link {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border-color: var(--primary);
                    color: white;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .pagination-charte .page-item .page-link:hover {
                    background: #f1f5f9;
                    border-color: var(--secondary);
                    color: var(--primary);
                    transform: translateY(-1px);
                }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }
                .summary-card-charte {
                    background: linear-gradient(135deg, #f8fafc, #ffffff);
                    padding: 1rem 1.25rem;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                }
                .categorie-avatar-charte {
                    width: 52px; height: 52px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    color: white; font-weight: 700; font-size: 1.2rem;
                    flex-shrink: 0;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.25);
                }
                .alert-warning-charte {
                    border: none; background: #fffbeb;
                    border-left: 4px solid var(--warning);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(245,158,11,0.08);
                }
                .alert-warning-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(245,158,11,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--warning); font-size: 1.5rem;
                }

                /* ===== INFO BANNER ===== */
                .info-banner-charte {
                    display: flex;
                    align-items: flex-start;
                    gap: 1rem;
                    padding: 1rem 1.25rem;
                    background: #f8fafc;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                }
                .info-banner-icon-charte {
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.3rem;
                    flex-shrink: 0;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-warning {
                    background: linear-gradient(135deg, var(--warning), #d97706);
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(245,158,11,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-warning:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(245,158,11,0.4); color: white;
                }
                .btn-modern-outline:disabled,
                .btn-modern-primary:disabled,
                .btn-modern-warning:disabled { opacity: 0.6; transform: none; }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .pagination-wrapper-charte { flex-direction: column; align-items: stretch; }
                    .pagination-info-charte { text-align: center; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}
